import type { IViewItemsViewModelController, IMeasurementViewModel } from '@/engine/app/interfaces';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import { iocContainer, TYPES } from '@/engine/ioc';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { IImagesPortContext } from '@/engine/app/modules/images/interfaces';
import type { SliceType } from '@/engine/app/modules/images/interfaces';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { AreaMeasurementBasePlaneTuner } from '@/engine/PlanningAlgorithm/Tuners/AreaMeasurementBasePlaneTuner';
import * as THREE from 'three';
import * as GeometryHelper from '@/engine/utils/geometryHelper';
import type { ICommandProcessor } from '../app/modules/core/interfaces/Command';
import { reaction } from 'mobx';
import { AutoDisposer } from '../Utilities/AutoDisposer';

export interface ISelectionReactionStrategy {
  reactOnSelection: (descriptor: string) => void;
}

export class ImagesAndAreaDependencyController implements IDisposable {
  private readonly _imagesService: ImagesService;

  private _viewModel: IMeasurementViewModel;

  private readonly _controller: IViewItemsViewModelController;

  private readonly _areaTuner: AreaMeasurementBasePlaneTuner;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private _subscriptions: DisposablesBasket = new DisposablesBasket();

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _sliceType: SliceType;

  private _previousReadyForSliding = false;

  private _undoData: { normal: THREE.Vector3; origin: THREE.Vector3 } | null = null;

  public constructor(
    viewModel: IMeasurementViewModel,
    imagesService: ImagesService,
    planningAlgorithm: PlanningAlgorithm,
    imagesPortContext: IImagesPortContext,
    sliceType: SliceType,
    commandProcessor: ICommandProcessor
  ) {
    this._imagesService = imagesService;
    this._planningAlgorithm = planningAlgorithm;
    this._viewModel = viewModel;
    this._areaTuner = new AreaMeasurementBasePlaneTuner(this._planningAlgorithm);
    this._controller = iocContainer.get<IViewItemsViewModelController>(TYPES.IViewItemsViewModelController);
    this._commandProcessor = commandProcessor;
    this._sliceType = sliceType;

    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
    this._subscriptions.add(imagesPortContext.addOnSliceCSChangedListener(this._onSliceChanged.bind(this), true));

    this._subscriptions.add(
      new AutoDisposer(
        reaction(
          () => viewModel.readyForSliding,
          (isReady) => {
            if (isReady && !this._previousReadyForSliding) {
              const { normal, origin } = this._areaTuner.getValue(this._viewModel.descriptor);
              this._undoData = { normal, origin };
            } else if (!isReady && this._previousReadyForSliding && this._undoData) {
              const { normal: undoNormal, origin: undoOrigin } = this._undoData;
              const { normal: redoNormal, origin: redoOrigin } = this._areaTuner.getValue(this._viewModel.descriptor);

              const isEqual = undoNormal.equals(redoNormal) && undoOrigin.equals(redoOrigin);
              if (!isEqual) {
                this._commandProcessor.addAnonymousCommand(
                  async (): Promise<undefined> => {
                    await this._areaTuner.setValueAsync(
                      this._viewModel.descriptor,
                      redoNormal.clone(),
                      redoOrigin.clone()
                    );
                    this._controller.locateMeasurementByDescriptor(this._viewModel.descriptor);
                  },
                  async (): Promise<undefined> => {
                    await this._areaTuner.setValueAsync(
                      this._viewModel.descriptor,
                      undoNormal.clone(),
                      undoOrigin.clone()
                    );
                    this._controller.locateMeasurementByDescriptor(this._viewModel.descriptor);
                  }
                );
              }
              this._undoData = null;
            }

            this._previousReadyForSliding = isReady;
          }
        )
      )
    );
  }

  private async _onSliceChanged(sliceCS: THREE.Matrix4): Promise<void> {
    if (!this._isViewModelReadyForSliding()) return;

    try {
      const areaModel = await this._getAreaModel();

      if (!this._shouldAdjustSlice(areaModel, sliceCS)) {
        const normal = new THREE.Vector3(0, 0, 1).transformDirection(sliceCS);
        const pointToMove = this._imagesService.getSlicesIntersectionPoint();
        await this._areaTuner.setValueAsync(this._viewModel.descriptor, normal, pointToMove);
      }
    } catch (error: unknown) {
      console.warn('Error during slice adjustment:', error);
    }
  }

  private _isViewModelReadyForSliding(): boolean {
    return (
      this._viewModel.isSelected &&
      this._viewModel.isTunable &&
      this._viewModel.isBeingEdited &&
      this._viewModel.readyForSliding
    );
  }

  private async _getAreaModel(): Promise<DT.AreaMeasurement | undefined> {
    return this._planningAlgorithm.getValueAsync(this._viewModel.descriptor, DT.AreaMeasurement);
  }

  private _shouldAdjustSlice(areaModel: DT.AreaMeasurement | undefined, sliceCS: THREE.Matrix4): boolean {
    return !!areaModel && this._isAreaOnSlice(areaModel, sliceCS);
  }

  private _isAreaOnSlice(areaModel: DT.AreaMeasurement, sliceCS: THREE.Matrix4): boolean {
    return areaModel.basePts.every((point: THREE.Vector3) => {
      return GeometryHelper.isPointNearPlane(point, sliceCS, 0.1);
    });
  }

  public dispose(): void {
    this._subscriptions.dispose();
  }
}
